// Decorative engineering backdrops. All purely presentational and aria-hidden.

export function BlueprintGrid({ className = "", light = false }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${light ? "blueprint-grid-light" : "blueprint-grid"} ${className}`}
    />
  );
}

// Perspective railway track with catenary wire and a travelling signal pulse.
export function TrackPerspective({ className = "" }) {
  const sleepers = [...Array(10)].map((_, i) => {
    const t = i / 10;
    const y = 555 - Math.pow(t, 1.55) * 470;
    const halfW = 360 * Math.pow(1 - t, 1.1);
    return { x1: 720 - halfW, y, x2: 720 + halfW, o: 0.5 * (1 - t), w: 2.4 * (1 - t) + 0.4 };
  });
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 1440 560"
      preserveAspectRatio="xMidYMax slice"
      fill="none"
    >
      <defs>
        <linearGradient id="railG" x1="720" y1="560" x2="720" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#34D399" stopOpacity="0.95" />
          <stop offset="1" stopColor="#38BDF8" stopOpacity="0.12" />
        </linearGradient>
        <radialGradient id="horizonG" cx="50%" cy="100%" r="65%">
          <stop offset="0" stopColor="#10B981" stopOpacity="0.12" />
          <stop offset="1" stopColor="#10B981" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1440" height="560" fill="url(#horizonG)" />
      {sleepers.map((s, i) => (
        <line key={i} x1={s.x1} y1={s.y} x2={s.x2} y2={s.y} stroke="#3A506B" strokeOpacity={s.o} strokeWidth={s.w} />
      ))}
      <path d="M40 560 C 500 430, 660 300, 714 55" stroke="url(#railG)" strokeWidth="3" strokeLinecap="round" />
      <path d="M1400 560 C 940 430, 780 300, 726 55" stroke="url(#railG)" strokeWidth="3" strokeLinecap="round" />
      {/* catenary masts + contact wire */}
      <path d="M300 470 V 150 M1140 470 V 150 M720 300 V 40" stroke="#3A506B" strokeWidth="2.5" strokeOpacity="0.55" />
      <path
        d="M0 150 H 300 M300 150 C 520 120, 920 120, 1140 150 M1140 150 H 1440"
        stroke="#38BDF8"
        strokeOpacity="0.35"
        strokeWidth="1.6"
        strokeDasharray="7 7"
      />
      <circle r="5" fill="#34D399" className="motion-sensitive">
        <animateMotion dur="7s" repeatCount="indefinite" path="M40 560 C 500 430, 660 300, 714 55" />
      </circle>
      <circle r="4" fill="#38BDF8" className="motion-sensitive">
        <animateMotion dur="9s" begin="1.6s" repeatCount="indefinite" path="M1400 560 C 940 430, 780 300, 726 55" />
      </circle>
      <circle cx="714" cy="55" r="6" fill="#10B981" className="animate-node-pulse motion-sensitive" />
    </svg>
  );
}

// Floating particles, deterministically placed for a calm field of drifting nodes.
const PARTICLES = [...Array(16)].map((_, i) => ({
  left: (i * 61 + 13) % 100,
  top: (i * 37 + 7) % 100,
  dur: 6 + (i % 5) * 2,
  delay: (i % 7) * 0.8,
}));

export function Particles({ className = "" }) {
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className={`motion-sensitive absolute h-1 w-1 rounded-full animate-floaty ${
            i % 3 === 0 ? "bg-brand-emeraldLight/70" : "bg-brand-blueGlow/60"
          }`}
          style={{ left: `${p.left}%`, top: `${p.top}%`, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }}
        />
      ))}
    </div>
  );
}

// Horizontal animated line with a travelling glowing node — used as a section divider.
export function RailDivider({ className = "" }) {
  return (
    <div aria-hidden="true" className={`relative mx-auto h-px w-full max-w-[1240px] ${className}`}>
      <div className="absolute inset-0 bg-navy-900/10" />
      <span className="circuit-node absolute -top-[3px] h-[7px] w-[7px] rounded-full bg-brand-emeraldLight shadow-[0_0_14px_rgba(52,211,153,0.9)]" />
    </div>
  );
}
